import React, { FormEvent } from 'react';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';

export interface Props {
  onValueChange: (value: string) => void;
  onChange: (event: FormEvent<HTMLTextAreaElement>) => void;
  onFocus: (event: React.FocusEvent) => void;
  onBlur: (event?: React.FocusEvent<HTMLTextAreaElement>) => void;
  label?: string | JSX.Element;
  link?: JSX.Element;
  placeholder?: string;
  helper?: string;
  className?: string;
  success?: boolean;
  warning?: boolean;
  error?: boolean;
  disabled?: boolean;
  compact?: boolean;
  maxLength?: number;
  dark?: boolean;
  name?: string;
  value?: string | number | string[];
  required?: boolean;
  autofocus?: boolean;
  readOnly?: boolean;
  autoComplete?: string;
  invert?: boolean;
  hasHiddenLabel?: boolean;
  id?: string;
  rows?: number;
  showLength?: boolean;
}
class TextArea extends React.Component<Props> {
  inputContainer = React.createRef<HTMLDivElement>();

  private input = React.createRef<HTMLTextAreaElement>();

  private id: string;

  static defaultProps = {
    onChange: () => {},
    onValueChange: () => {},
    onFocus: () => {},
    onBlur: () => {},
    placeholder: '',
    helper: '',
    label: '',
    success: false,
    warning: false,
    error: false,
    disabled: false,
    hasHiddenLabel: false,
    rows: 1,
  };

  constructor(props: Props) {
    super(props);

    this.id = props.id ? props.id : uniqueId('textarea-');
  }

  onChange = (event: FormEvent<HTMLTextAreaElement>) => {
    if (this.props.disabled) {
      return;
    }

    this.props.onChange(event);
    this.props.onValueChange(event.currentTarget.value);
  };

  render() {
    const { compact, dark, disabled, error, warning, success, value, invert, ...rest } = this.props;
    const { label, link, helper, required, onValueChange, className, hasHiddenLabel, id, showLength, ...props } = rest;

    const classes = classNames('input', 'textarea', this.props.className, {
      compact,
      dark,
      disabled,
      error,
      warning,
      success,
      invert,
    });

    const canShowLimitCount = showLength && this.props.maxLength && this.props.maxLength > 0 && (typeof value === 'number' || typeof value === 'string');

    return (
      <div className={classes}>
        {(label || link) && (
          <div className={classNames('label-container', { 'visually-hidden': hasHiddenLabel })}>
            <label htmlFor={this.id}>
              {label}
              {required ? '*' : null}
            </label>

            {link && <label htmlFor={this.id}>{link}</label>}
          </div>
        )}

        <div className="input-container d-flex flex-cell" ref={this.inputContainer}>
          <textarea
            {...props}
            id={this.id}
            ref={this.input}
            disabled={disabled}
            autoCorrect="off"
            spellCheck={false}
            onChange={this.onChange}
            onFocus={this.props.onFocus}
            onBlur={this.props.onBlur}
            maxLength={this.props.maxLength}
            value={value}
          />
          {canShowLimitCount && (
            <span className="limit-count">
              {String(value).length}/{this.props.maxLength}
            </span>
          )}
        </div>

        {helper && <span className="input-helper-text">{helper}</span>}
      </div>
    );
  }
}

export default TextArea;
